/**
 * FlangerControls Component
 * 
 * Specialized control panel for the Flanger effect.
 * Displays 5 rotary knobs with waveform selector and stereo phase control.
 */

import { RotaryKnob } from '../../ui/RotaryKnob';
import { PresetSelector } from './PresetSelector';
import type { EffectInstance, EffectDefinition } from '../../../services/effects/EffectsManager';
import { getParameterDescription } from '../../../constants/effectDescriptions';
import './FlangerControls.css';

/**
 * Props for FlangerControls component
 */
export interface FlangerControlsProps {
  /** Effect instance */
  effect: EffectInstance;
  /** Effect definition */
  effectDefinition: EffectDefinition;
  /** Callback when parameter changes */
  onParameterChange: (parameterId: string, value: number) => void;
  /** Callback when multiple parameters change at once (for presets) */
  onParametersChange?: ((parameters: Record<string, number>) => void) | undefined;
  /** Whether controls are disabled */
  disabled?: boolean;
}

/**
 * FlangerControls component
 * 
 * Layout:
 * - Top row: 3 knobs (rate, depth, delay)
 * - Bottom row: 2 knobs (feedback, mix)
 * 
 * @param props - Component props
 * @returns FlangerControls component
 */
export function FlangerControls({
  effect,
  effectDefinition,
  onParameterChange,
  onParametersChange,
  disabled = false,
}: FlangerControlsProps) {
  /**
   * Get parameter definition by ID
   */
  const getParamDef = (id: string) => {
    return effectDefinition.parameters.find(p => p.id === id);
  };

  /**
   * Get parameter value
   */
  const getParamValue = (id: string) => {
    const paramDef = getParamDef(id);
    return effect.parameters[id] ?? paramDef?.default ?? 0;
  };

  /**
   * Handle preset selection
   */
  const handlePresetSelect = (presetParameters: Record<string, number>) => {
    // Use batch update if available, otherwise fall back to individual updates
    if (onParametersChange) {
      onParametersChange(presetParameters);
    } else {
      // Fallback: Apply parameters individually with delays
      Object.entries(presetParameters).forEach(([paramId, value], index) => {
        setTimeout(() => {
          onParameterChange(paramId, value);
        }, index * 10);
      });
    }
  };

  return (
    <div className="flanger-controls">
      {/* Preset Selector */}
      <PresetSelector
        effectType={effect.type}
        onPresetSelect={handlePresetSelect}
        disabled={disabled}
      />

      {/* Top row: Rate, Depth, Delay */}
      <div className="flanger-controls-row">
        {['rate', 'depth', 'delay'].map((paramId) => {
          const paramDef = getParamDef(paramId);
          if (!paramDef) return null;
          
          const value = getParamValue(paramId);
          const tooltip = getParameterDescription(effect.type, paramId);
          
          return (
            <RotaryKnob
              key={paramId}
              value={value}
              min={paramDef.min}
              max={paramDef.max}
              default={paramDef.default}
              step={paramDef.step}
              label={paramDef.name}
              {...(paramDef.unit && { unit: paramDef.unit })}
              onChange={(val) => onParameterChange(paramId, val)}
              disabled={disabled}
              size="medium"
              {...(tooltip && { tooltip })}
            />
          );
        })}
      </div>

      {/* Bottom row: Feedback, Mix */}
      <div className="flanger-controls-row">
        {['feedback', 'mix'].map((paramId) => {
          const paramDef = getParamDef(paramId);
          if (!paramDef) return null;
          
          const value = getParamValue(paramId);
          const tooltip = getParameterDescription(effect.type, paramId);
          
          return (
            <RotaryKnob
              key={paramId}
              value={value}
              min={paramDef.min}
              max={paramDef.max}
              default={paramDef.default}
              step={paramDef.step}
              label={paramDef.name}
              {...(paramDef.unit && { unit: paramDef.unit })}
              onChange={(val) => onParameterChange(paramId, val)}
              disabled={disabled}
              size="medium"
              {...(tooltip && { tooltip })}
            />
          );
        })}
      </div>
    </div>
  );
}
